import React from 'react';
import { useApp } from '../../context/AppContext';
import { 
  Users, 
  Star, 
  MapPin, 
  Calendar,
  Phone,
  CheckCircle2,
} from 'lucide-react';

// Stat Card Component
const StatCard = ({ label, value, color = 'burgundy' }) => {
  const colorStyles = {
    burgundy: 'border-burgundy-100',
    green: 'border-green-100',
    blue: 'border-blue-100',
    yellow: 'border-yellow-100',
  };

  return (
    <div className={`bg-white rounded-2xl border ${colorStyles[color]} p-5`}>
      <p className="text-4xl font-serif font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-1 uppercase tracking-wide">{label}</p>
    </div>
  );
};

const OfficersPage = () => {
  const { careOfficers, visits, performance } = useApp();
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Lagos' });
  const activeToday = new Set(
    visits
      .filter(visit => {
        const scheduled = visit.scheduledStart || visit.scheduledDate;
        if (!scheduled) return false;
        return new Date(scheduled).toLocaleDateString('en-CA', { timeZone: 'Africa/Lagos' }) === today;
      })
      .map(visit => visit.careOfficerId)
      .filter(Boolean)
  ).size;
  const ratingValues = careOfficers
    .map(officer => Number(officer.rating || performance.find(item => item.officerId === officer.id)?.rating || 0))
    .filter(Boolean);
  const completionValues = careOfficers
    .map(officer => Number(officer.completionRate || performance.find(item => item.officerId === officer.id)?.completionRate || 0))
    .filter(Boolean);
  const avgRating = ratingValues.length ? (ratingValues.reduce((sum, value) => sum + value, 0) / ratingValues.length).toFixed(1) : '0.0';
  const avgCompletion = completionValues.length ? `${(completionValues.reduce((sum, value) => sum + value, 0) / completionValues.length).toFixed(1)}%` : '0.0%';

  return (
    <div className="min-h-screen bg-cream" data-testid="officers-page">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div>
            <h1 className="font-serif text-2xl font-bold text-gray-900">Care Officers</h1>
            <p className="text-sm text-gray-500 mt-1">Manage and monitor care officer performance</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <StatCard label="Total Officers" value={careOfficers.length} color="burgundy" />
          <StatCard label="Active Today" value={activeToday} color="green" />
          <StatCard label="Avg. Rating" value={avgRating} color="yellow" />
          <StatCard label="Avg. Completion" value={avgCompletion} color="blue" />
        </div>

        {/* Officers List */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-serif text-lg font-semibold text-gray-900">All Care Officers</h2>
          </div>
          
          <div className="divide-y divide-gray-100">
            {careOfficers.map(officer => {
              const officerPerformance = performance.find(item => item.officerId === officer.id) || {};
              const firstName = officer.firstName || '';
              const lastName = officer.lastName || '';
              return (
              <div key={officer.id} className="p-5 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-yellow-100 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {firstName[0] || 'C'}{lastName[0] || 'O'}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-gray-900">{firstName} {lastName}</h3>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">{officer.status || 'Active'}</span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-2">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        {officer.rating || officerPerformance.rating || 0} Rating
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        {officer.completionRate || officerPerformance.completionRate || 0}% Completion
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-burgundy-500" />
                        {officer.totalVisits || officerPerformance.totalVisits || 0} Visits
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-blue-500" />
                        {officer.activeAssignments || 0} Assignments
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mt-3">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {officer.coverageZone?.state} - {officer.coverageZone?.lgas?.join(', ')}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-200 transition-colors flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Contact
                    </button>
                    <button className="px-4 py-2 bg-burgundy-600 text-white text-sm font-medium rounded-full hover:bg-burgundy-700 transition-colors">
                      View Profile
                    </button>
                  </div>
                </div>
              </div>
            );})}
            {careOfficers.length === 0 && (
              <div className="p-10 text-center text-gray-500">
                No care officers found. Create care officers before generating schedules.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficersPage;
